import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

const API = process.env.REACT_APP_SERVER_URL;

const PLATFORM_LABEL = { medium: "Medium", hashnode: "Hashnode" };

function copy(text, label) {
  navigator.clipboard
    .writeText(text || "")
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.error("Could not copy"));
}

/**
 * The body is stored as markdown with [[IMAGE_n]] markers. Here we split
 * on those markers so each image prompt renders as a card sitting exactly
 * where the image belongs — the same layout as the email, so what he sees
 * in the admin matches what landed in his inbox.
 */
function BodyWithImages({ body, images }) {
  const byId = useMemo(
    () =>
      new Map(
        (Array.isArray(images) ? images : []).map((img) => [
          String(img.id),
          img,
        ]),
      ),
    [images],
  );

  const segments = String(body || "").split(/\[\[IMAGE_(\d+)\]\]/g);

  return (
    <Stack spacing={1.5}>
      {segments.map((segment, index) => {
        if (index % 2 === 1) {
          const image = byId.get(String(segment));
          if (!image) return null;
          return (
            <Paper
              key={`img-${segment}`}
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "#fff8ec",
                borderColor: "#f2cf94",
                borderLeft: "4px solid #e59a2f",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="overline"
                  sx={{ color: "#b3730f", fontWeight: 700 }}
                >
                  Image {image.id} · generate and paste here
                </Typography>
                <Tooltip title="Copy prompt">
                  <IconButton
                    size="small"
                    onClick={() => copy(image.prompt, "Prompt")}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              {image.purpose && (
                <Typography variant="body2" sx={{ color: "#6b5327", mb: 1 }}>
                  {image.purpose}
                </Typography>
              )}
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: "#fff",
                  borderColor: "#f0dcbb",
                  fontFamily: "monospace",
                  fontSize: 13,
                  whiteSpace: "pre-wrap",
                }}
              >
                {image.prompt}
              </Paper>
              {image.alt && (
                <Typography
                  variant="caption"
                  sx={{ color: "#8a7a5c", display: "block", mt: 1 }}
                >
                  Alt text: {image.alt}
                </Typography>
              )}
            </Paper>
          );
        }

        const chunk = segment.trim();
        if (!chunk) return null;
        return (
          <Typography
            key={`text-${index}`}
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              lineHeight: 1.75,
              fontFamily: "monospace",
              fontSize: 13.5,
            }}
          >
            {chunk}
          </Typography>
        );
      })}
    </Stack>
  );
}

export default function BlogDraftEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    meta_description: "",
    tags: "",
    body_markdown: "",
    first_comment: "",
    canonical_url: "",
    published_url: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/blog-studio/drafts/${id}`, {
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Unable to load draft");
      }
      setDraft(data.data);
      setForm({
        title: data.data.title || "",
        subtitle: data.data.subtitle || "",
        meta_description: data.data.meta_description || "",
        tags: (data.data.tags || []).join(", "),
        body_markdown: data.data.body_markdown || "",
        first_comment: data.data.first_comment || "",
        canonical_url: data.data.canonical_url || "",
        published_url: data.data.published_url || "",
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/blog-studio/drafts/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: form.title,
          subtitle: form.subtitle,
          meta_description: form.meta_description,
          tags: form.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
          body_markdown: form.body_markdown,
          first_comment: form.first_comment || null,
          canonical_url: form.canonical_url || null,
          published_url: form.published_url || null,
          ...extra,
        }),
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.message || "Save failed");
      setDraft(data.data);
      toast.success(extra.status === "published" ? "Marked as published" : "Saved");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const resend = async () => {
    try {
      const res = await fetch(
        `${API}/admin/blog-studio/drafts/${id}/resend`,
        { method: "POST", credentials: "include" },
      );
      const data = await res.json();
      if (!data?.success) throw new Error(data?.message || "Send failed");
      toast.success("Email sent");
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!draft) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Draft not found.</Alert>
      </Box>
    );
  }

  const isHashnode = draft.platform === "hashnode";
  const subtitleLabel = isHashnode ? "SEO title" : "Subtitle";

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => navigate("/blog-studio")}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" fontWeight={700}>
                {PLATFORM_LABEL[draft.platform]} article
              </Typography>
              <Chip size="small" label={draft.status} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {draft.run_date} · {draft.word_count || 0} words ·{" "}
              {(draft.image_prompts || []).length} images
              {draft.source_kind ? ` · from ${draft.source_kind}` : ""}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<SendOutlinedIcon />} onClick={resend}>
            Email to me
          </Button>
          <Button
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => save({ status: "published" })}
            disabled={saving}
          >
            Mark published
          </Button>
          <Button variant="contained" onClick={() => save()} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
        <Paper variant="outlined" sx={{ p: 2.5, flex: 1, minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary">
            Fields to paste into {PLATFORM_LABEL[draft.platform]}
          </Typography>
          <Stack spacing={2} sx={{ mt: 1.5 }}>
            <TextField
              label="Title"
              fullWidth
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              InputProps={{
                endAdornment: (
                  <IconButton
                    size="small"
                    onClick={() => copy(form.title, "Title")}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
            <TextField
              label={subtitleLabel}
              fullWidth
              multiline
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              helperText={
                isHashnode
                  ? `${form.subtitle.length} chars — keep under 60 for the SEO title`
                  : `${form.subtitle.length} chars — shows under the title in every Medium feed`
              }
            />
            <TextField
              label="Meta description"
              fullWidth
              multiline
              value={form.meta_description}
              onChange={(e) =>
                setForm({ ...form, meta_description: e.target.value })
              }
              helperText={`${form.meta_description.length} chars — aim for 150-160`}
            />
            <TextField
              label="Tags"
              fullWidth
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              helperText="Comma separated"
            />
            <Divider />
            <TextField
              label="Body (markdown)"
              fullWidth
              multiline
              minRows={18}
              value={form.body_markdown}
              onChange={(e) =>
                setForm({ ...form, body_markdown: e.target.value })
              }
              InputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
              helperText="[[IMAGE_n]] markers show where each generated image goes"
            />
            <Button
              startIcon={<ContentCopyIcon />}
              onClick={() => copy(form.body_markdown, "Body")}
            >
              Copy body
            </Button>
            <Divider />
            <TextField
              label="First comment"
              fullWidth
              multiline
              minRows={3}
              value={form.first_comment}
              onChange={(e) =>
                setForm({ ...form, first_comment: e.target.value })
              }
              helperText={`${form.first_comment.length} chars — paste this under the post right after publishing`}
              InputProps={{
                endAdornment: (
                  <IconButton
                    size="small"
                    onClick={() => copy(form.first_comment, "First comment")}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
            <TextField
              label="Canonical URL"
              fullWidth
              value={form.canonical_url}
              onChange={(e) =>
                setForm({ ...form, canonical_url: e.target.value })
              }
              helperText="Only needed if this exact article is already published somewhere else. Usually blank."
            />
            <TextField
              label="Published URL"
              fullWidth
              value={form.published_url}
              onChange={(e) =>
                setForm({ ...form, published_url: e.target.value })
              }
              helperText="Paste the live link once it's up — useful for setting canonicals later"
            />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, flex: 1, minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary">
            Preview with image prompts in place
          </Typography>
          {draft.publishing_note && (
            <Alert severity="error" sx={{ my: 1.5, whiteSpace: "pre-wrap" }}>
              <strong>Check these before posting</strong>
              {"\n"}
              {draft.publishing_note}
            </Alert>
          )}
          {form.first_comment && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                my: 1.5,
                bgcolor: "#edf9f0",
                borderColor: "#bfe4c9",
                borderLeft: "4px solid #2e9e55",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="overline"
                  sx={{ color: "#1f7a41", fontWeight: 700 }}
                >
                  Post this as the first comment
                </Typography>
                <Tooltip title="Copy first comment">
                  <IconButton
                    size="small"
                    onClick={() => copy(form.first_comment, "First comment")}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: "#fff",
                  borderColor: "#cfe8d7",
                  fontFamily: "monospace",
                  fontSize: 13.5,
                  whiteSpace: "pre-wrap",
                }}
              >
                {form.first_comment}
              </Paper>
              <Typography
                variant="caption"
                sx={{ color: "#5d8a6e", display: "block", mt: 1 }}
              >
                Copy as-is. Any link in here was checked and is live.
              </Typography>
            </Paper>
          )}
          {form.canonical_url && (
            <Alert severity="info" sx={{ my: 1.5 }}>
              <strong>Canonical URL</strong> — paste{" "}
              <code>{form.canonical_url}</code> into{" "}
              {isHashnode
                ? "Hashnode article settings \u2192 “Original article URL”"
                : "Medium story settings \u2192 “Advanced settings” \u2192 canonical link"}
              .
            </Alert>
          )}
          <Box sx={{ mt: 1.5 }}>
            <BodyWithImages
              body={form.body_markdown}
              images={draft.image_prompts}
            />
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}
